import React from 'react';
import PropTypes from 'prop-types';
import OmForm from '../../ui/OmForm';
import OmTextField from '../../ui/OmTextField';
import OmSelect from '../../ui/OmSelect';
import OmSelectOption from '../../ui/OmSelectOption';
import OmCheckbox from '../../ui/OmCheckbox';

/**
 * Step 1: Claim Metadata Component
 * Handles policy number, date filed, claim type selection, and development-only fields
 */
const Step1ClaimMetadata = ({
  formData,
  errors,
  validationErrors,
  businessTypes,
  isLoading,
  onInputChange,
  onFieldBlur,
  onNext,
  onPrevious,
  currentStep,
  schema
}) => {
  // Handle form submission for this step
  const handleStepSubmit = (e) => {
    e.preventDefault();
    if (onNext) {
      onNext(e);
    }
  };

  // Render loading state if schema is not available
  if (!schema || !schema.baseFields) {
    return (
      <div className="text-center py-12">
        <div className="mb-4">
          <svg className="w-16 h-16 mx-auto text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <p className="text-steel text-lg">
          {!schema ? 'Schema not loaded yet' : 'Schema structure incomplete'}
        </p>
        <p className="text-steel text-sm mt-2">
          {!schema 
            ? 'Please wait while we load the form schema...' 
            : 'Missing required form field definitions. Please contact technical support.'}
        </p>
      </div>
    );
  }

  // Additional validation for critical fields
  if (!schema.baseFields.policyNumber) {
    console.error('❌ Step1ClaimMetadata: Critical field policyNumber is missing from schema.baseFields');
    return (
      <div className="text-center py-12">
        <div className="mb-4">
          <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <p className="text-red-700 text-lg font-semibold">Form Configuration Error</p>
        <p className="text-red-600 text-sm mt-2">
          Required field definitions are missing. Please contact technical support.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OmForm
        titleText="Claim Metadata"
        descriptionText="Please provide your policy details and claim information"
        onSubmit={handleStepSubmit}
        formId="claim-metadata-form"
        submitButtonLabel="Continue to Claimant Information"
        hideSubmitButton={false}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Policy Number Field */}
          <div className="space-y-2">
            <OmTextField
              label="Policy Number"
              name="policyNumber"
              value={formData.policyNumber || ''}
              onChange={(e) => onInputChange('policyNumber', e.target.value)}
              onBlur={(e) => onFieldBlur && onFieldBlur('policyNumber', e.target.value)}
              placeholder="Enter policy number..."
              required={true}
              error={errors.policyNumber}
              disabled={isLoading}
              className={validationErrors?.policyNumber ? 'validation-warning' : ''}
            />
          </div>

          {/* Member Staff Number Field */}
          <div className="space-y-2">
            <OmTextField
              label="Member Staff Number"
              name="memberStaffNumber"
              value={formData.memberStaffNumber || ''}
              onChange={(e) => onInputChange('memberStaffNumber', e.target.value)}
              onBlur={(e) => onFieldBlur && onFieldBlur('memberStaffNumber', e.target.value)}
              placeholder="Enter staff number..."
              required={true}
              error={errors.memberStaffNumber}
              disabled={isLoading}
              helperText="Staff ID required for member details lookup"
            />
          </div>


          {/* Claim Type Selection */}
          <div className="space-y-2 lg:col-span-2">
            <OmSelect
              label="Claim Type"
              name="type"
              value={formData.type || ''}
              onChange={(e) => onInputChange('type', e.target.value)}
              required={true}
              error={errors.type}
              disabled={isLoading}
              helperText="Select the type of claim you are submitting"
            >
              <OmSelectOption value="">Select claim type...</OmSelectOption>
              {businessTypes.map(businessType => (
                <OmSelectOption 
                  key={businessType.claimType || businessType.code} 
                  value={businessType.claimType || businessType.code}
                >
                  {businessType.displayName || businessType.description}
                </OmSelectOption>
              ))}
            </OmSelect>
          </div>



        </div>

        <div slot="actions">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={onPrevious}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Previous
            </button>
          )}
        </div>
      </OmForm>
    </div>
  );
};

Step1ClaimMetadata.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  validationErrors: PropTypes.object,
  businessTypes: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onFieldBlur: PropTypes.func,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  currentStep: PropTypes.number.isRequired,
  schema: PropTypes.object
};

Step1ClaimMetadata.defaultProps = {
  validationErrors: {},
  onFieldBlur: null,
  onPrevious: null,
  schema: null
};

export default Step1ClaimMetadata;